import { useMemo, useReducer, useCallback } from 'react';

export type ActionFunction<Payload, State> = (payload: Payload, state: State) => Partial<State>;
export type ReducerMap<State> = {
  initialize?: (payload: any) => State,
} & {
  [key: Exclude<string, "initialize">]: (payload: any, state: State) => Partial<State> | void;
};

export type DispatchMap<T> = {
  [K in keyof T]: T[K] extends (payload: infer P, state: any) => any
    ? (
      undefined extends P
      ? (payload?: P) => void
      : (payload: P) => void
    )
    : never;
};

export type InitialState<State, R extends ReducerMap<State>> = ( 
  R["initialize"] extends (args: infer InitalArgs) => State
  ? InitalArgs
  : State
);

function useActionReducer<State, R extends ReducerMap<State>>(
  actions: R,
  initialState: InitialState<State, R>,
  traceTag?: string
): [State, DispatchMap<R>] {
  const reducer = useCallback(
    (state: State, { action, payload }: { action: string, payload: any }) => {
      let newState;

      if (actions[action]) {
        if (traceTag) console.info(`ActionReducer[${traceTag}].${action}`, { payload, state });
        newState = actions[action](payload, state);
      } else {
        console.warn(`unknown action sent to reducer ${traceTag}: ${action}`);
      }

      if (!newState || Object.entries(newState).every(([k, v]) => v === (state as any)[k])) {
        return state;
      }

      return { ...state, ...newState };
    },
    [actions]
  );

  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    actions.initialize ? ((x: any) => (actions.initialize as any)(x, {} as any)) as any : undefined
  );

  const dispatchAction = useMemo(
    () =>
      Object.keys(actions).reduce((_, action) => {
        _[action] = (payload: any) => dispatch({ action, payload });

        return _;
      }, {} as any),
    [actions, dispatch]
  ) as DispatchMap<R>;

  return [state, dispatchAction];
}


export class ReducerMapBuilder<State, BuiltType extends object = {}> {
  obj: BuiltType;
  constructor() {
    this.obj = {} as any;
  }
  build() {
    return this.obj;
  }
  action<Action extends string, Payload>(action: Action, fn: ActionFunction<Payload, State>) {
    (this.obj as any)[action] = fn;
    return this as ReducerMapBuilder<State, BuiltType & {
      [K in Action]: ActionFunction<Payload, State>
    }>;
  }
}


export default useActionReducer;
